import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { shouldExcludeFromAnalytics } from '@/lib/analyticsExclusions';

const CHANNEL_NAME = 'website-visitors';
const PRESENCE_REFRESH_INTERVAL = 15000; // 15 seconds - faster heartbeat for reliable tracking
const INIT_DELAY = 1500; // Short delay to avoid competing with initial render, but fast enough to capture most visitors

// Generate a persistent session ID for this browser tab — shared with usePageAnalytics
const getSessionId = () => {
  // Check both keys for backward compat, prefer visitor_session_id
  let sessionId = sessionStorage.getItem('visitor_session_id') || sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `visitor_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }
  // Store under both keys so both hooks use the same ID
  sessionStorage.setItem('visitor_session_id', sessionId);
  sessionStorage.setItem('analytics_session_id', sessionId);
  return sessionId;
};

// Get or create a persistent visitor ID across sessions (localStorage)
const getVisitorId = (): string => {
  let visitorId = localStorage.getItem('lt_visitor_id');
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem('lt_visitor_id', visitorId);
    localStorage.setItem('lt_first_visit', Date.now().toString());
  }
  return visitorId;
};

// Check if this is a returning visitor (has visited before in a previous session)
const getIsReturning = (): boolean => {
  const firstVisit = localStorage.getItem('lt_first_visit');
  const visitCount = parseInt(localStorage.getItem('lt_visit_count') || '0', 10);
  
  // Increment visit count once per session
  if (!sessionStorage.getItem('lt_visit_counted')) {
    localStorage.setItem('lt_visit_count', (visitCount + 1).toString());
    sessionStorage.setItem('lt_visit_counted', 'true');
    // On the first call of this session, visitCount reflects previous sessions
    // Returning if they had at least 1 previous session
    return visitCount > 0 && !!firstVisit;
  }
  
  // On subsequent calls within same session, visitCount already includes
  // this session's increment, so check > 1
  return visitCount > 1 && !!firstVisit;
};

// Extract VB number from URL path or session storage
const extractVBFromPath = (pathname: string): string | null => {
  // Check for pending VB lookup (from resume-booking page)
  if (pathname === '/resume-booking') {
    return sessionStorage.getItem('pending_vb_lookup') || null;
  }

  // Match /booking/:id patterns and try to get VB from session storage
  const bookingMatch = pathname.match(/\/booking\/([^/]+)/);
  if (bookingMatch) {
    const bookingId = bookingMatch[1];
    // Try to get VB from session storage (set by booking pages)
    const vbNumber = sessionStorage.getItem(`booking_vb_${bookingId}`);
    return vbNumber;
  }
  
  // Match /sign/:vbNumber patterns
  const signMatch = pathname.match(/\/sign\/(VB\d+)/i);
  if (signMatch) {
    return signMatch[1].toUpperCase();
  }
  
  // Match /job-inventory with VB in query or storage
  if (pathname === '/job-inventory') {
    return sessionStorage.getItem('current_job_vb') || null;
  }
  
  return null;
};

// Get workflow step from URL
const getWorkflowStepFromPath = (pathname: string): string | null => {
  if (pathname === '/resume-booking') return 'resume_booking';
  if (pathname.includes('/quote-review')) return 'quote_review';
  if (pathname.includes('/booking-details')) return 'booking_details';
  if (pathname.includes('/confirm')) return 'confirm_booking';
  if (pathname.includes('/insurance')) return 'insurance';
  if (pathname.includes('/review')) return 'customer_review';
  if (pathname.includes('/signature')) return 'signature';
  if (pathname.includes('/payment')) return 'payment';
  if (pathname.includes('/confirmation')) return 'completed';
  if (pathname === '/job-inventory') return 'updating_inventory';
  if (pathname.includes('/sign/')) return 'signing_document';
  return null;
};

// Get customer name from session storage (set by booking pages)
const getCustomerName = (): string | null => {
  return sessionStorage.getItem('visitor_customer_name');
};

// Heuristic: URLs only distributed via email/SMS — if entry is here with no
// other attribution signals, it's an email/SMS click.
const isEmailDistributedPath = (pathname: string): boolean => {
  if (/^\/booking\/[^/]+\/(quote-review|payment|confirm|review|signature|insurance|booking-details)/i.test(pathname)) return true;
  if (/^\/sign\/VB\d+/i.test(pathname)) return true;
  if (pathname === '/resume-booking') return true;
  if (pathname === '/customer-payment') return true;
  return false;
};

// Detect traffic source from URL params and referrer (cached per session)
const getTrafficSource = (): string => {
  const cached = sessionStorage.getItem('visitor_traffic_source');
  if (cached !== null) return cached;

  const search = window.location.search;
  const urlParams = new URLSearchParams(search);
  const utmSource = urlParams.get('utm_source');
  let source = 'direct';

  if (urlParams.get('gclid') || urlParams.get('gad_source')) {
    source = 'google_ads';
  } else if (utmSource === 'sms' || urlParams.get('utm_medium') === 'sms') {
    source = 'sms';
  } else if (utmSource === 'email' || utmSource === 'signature') {
    source = 'email';
  } else if (utmSource) {
    source = utmSource;
  } else if (urlParams.get('fbclid')) {
    source = 'facebook';
  } else if (document.referrer.includes('google')) {
    source = 'google_organic';
  } else if (document.referrer.includes('facebook') || document.referrer.includes('instagram')) {
    source = 'facebook';
  } else if (document.referrer.includes('brave')) {
    source = 'brave_search';
  } else if (document.referrer.includes('reddit')) {
    source = 'reddit';
  } else if (document.referrer.includes('bing')) {
    source = 'bing';
  } else if (document.referrer.includes('yahoo')) {
    source = 'yahoo';
  } else if (document.referrer && !document.referrer.includes(window.location.hostname) && !document.referrer.includes('lifetimemovers.us') && !document.referrer.includes('lovable')) {
    source = 'referral';
  } else if (isEmailDistributedPath(window.location.pathname)) {
    // Fallback: entry page only reachable via email/SMS link
    source = 'email';
  }

  sessionStorage.setItem('visitor_traffic_source', source);
  return source;
};

// Get referring domain from document.referrer (captured once per session)
const getReferringDomain = (): string | null => {
  // Check if we already captured it this session
  const cached = sessionStorage.getItem('visitor_referring_domain');
  if (cached !== null) return cached || null;
  
  try {
    const referrer = document.referrer;
    if (!referrer) {
      sessionStorage.setItem('visitor_referring_domain', '');
      return null;
    }
    
    // Parse the referrer URL to extract the domain
    const url = new URL(referrer);
    
    // Skip if referrer is our own domain
    if (url.hostname === window.location.hostname) {
      sessionStorage.setItem('visitor_referring_domain', '');
      return null;
    }
    if (url.hostname.includes('lifetimemovers.us')) {
      sessionStorage.setItem('visitor_referring_domain', '');
      return null;
    }
    if (url.hostname.includes('lovableproject.com')) {
      sessionStorage.setItem('visitor_referring_domain', '');
      return null;
    }
    if (url.hostname.includes('lovable.app')) {
      sessionStorage.setItem('visitor_referring_domain', '');
      return null;
    }
    
    sessionStorage.setItem('visitor_referring_domain', url.hostname);
    return url.hostname;
  } catch {
    sessionStorage.setItem('visitor_referring_domain', '');
    return null;
  }
};

export interface VisitorPresenceData {
  session_id: string;
  visitor_id: string;
  is_returning: boolean;
  page: string;
  page_history: string[];
  pages_viewed: number;
  vb_number: string | null;
  workflow_step: string | null;
  customer_name: string | null;
  referring_domain: string | null;
  traffic_source: string;
  entry_time: number;
  last_activity: number;
}

// Get or initialize page history from session storage
const getPageHistory = (): string[] => {
  try {
    const stored = sessionStorage.getItem('visitor_page_history');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const addToPageHistory = (page: string): string[] => {
  const history = getPageHistory();
  // Don't add duplicate consecutive pages
  if (history[history.length - 1] !== page) {
    history.push(page);
    // Keep only last 20 pages
    const trimmed = history.slice(-20);
    sessionStorage.setItem('visitor_page_history', JSON.stringify(trimmed));
    return trimmed;
  }
  return history;
};

// Client-side bot detection (mirrors usePageAnalytics patterns)
const BOT_UA_PATTERNS = [
  /googlebot/i, /bingbot/i, /baiduspider/i, /yandexbot/i, /duckduckbot/i,
  /bytespider/i, /petalbot/i, /sogou/i, /slurp/i,
  /bot\b/i, /crawler/i, /spider/i, /scraper/i, /headless/i,
  /python/i, /curl\//i, /wget\//i, /node-fetch/i, /go-http-client/i, /java\//i, /ruby/i, /perl/i, /libwww/i, /httpie/i, /axios\//i,
  /semrush/i, /ahrefs/i, /dataforseo/i, /screaming frog/i, /majestic/i,
  /facebookexternalhit/i, /twitterbot/i, /linkedinbot/i, /whatsapp/i, /telegrambot/i, /slackbot/i, /discordbot/i, /pinterest/i,
  /gptbot/i, /claudebot/i, /anthropic/i, /ccbot/i, /applebot/i,
  /nmap/i, /nikto/i, /sqlmap/i, /dirbuster/i, /burpsuite/i, /masscan/i, /zgrab/i, /censys/i, /shodan/i,
];

const isBot = (userAgent: string): boolean => {
  if (!userAgent || userAgent.length < 20) return true;
  return BOT_UA_PATTERNS.some(p => p.test(userAgent));
};

export function useVisitorPresence() {
  const location = useLocation();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const sessionId = useRef(getSessionId());
  const entryTime = useRef(Date.now());
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isReady, setIsReady] = useState(false);
  const isBotSession = useRef(isBot(navigator.userAgent));
  const pathnameRef = useRef(location.pathname);

  // Keep pathname ref in sync
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  // 1. Break the deadlock: set isReady after INIT_DELAY, independent of channel
  useEffect(() => {
    if (isBotSession.current) return;
    const timer = setTimeout(() => {
      console.log('[Presence] Ready after init delay');
      setIsReady(true);
    }, INIT_DELAY);
    return () => clearTimeout(timer);
  }, []);

  // 2. trackPresence builds payload and calls channel.track()
  const trackPresence = useCallback(async (channel: ReturnType<typeof supabase.channel>) => {
    if (isBotSession.current) return;
    const pathname = pathnameRef.current;
    if (shouldExcludeFromAnalytics(pathname)) return;

    const pageHistory = addToPageHistory(pathname);
    const data: VisitorPresenceData = {
      session_id: sessionId.current,
      visitor_id: getVisitorId(),
      is_returning: getIsReturning(),
      page: pathname,
      page_history: pageHistory,
      pages_viewed: pageHistory.length,
      vb_number: extractVBFromPath(pathname) || null,
      workflow_step: getWorkflowStepFromPath(pathname),
      customer_name: getCustomerName(),
      referring_domain: getReferringDomain(),
      traffic_source: getTrafficSource(),
      entry_time: entryTime.current,
      last_activity: Date.now(),
    };

    try {
      await channel.track(data);
      console.log('[Presence] Tracked:', pathname);
    } catch (err) {
      console.error('[Presence] Track error:', err);
    }
  }, []);

  // Helper to fully destroy the channel and clear refs
  const destroyChannel = useCallback(() => {
    if (channelRef.current) {
      console.log('[Presence] Destroying channel');
      channelRef.current.untrack();
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  // Helper to create the channel
  const initChannel = useCallback(() => {
    if (channelRef.current) return;

    console.log('[Presence] Creating channel for:', sessionId.current);

    const channel = supabase.channel(CHANNEL_NAME, {
      config: {
        presence: {
          // Key on the persistent visitor id (not the per-tab session id) so
          // multiple tabs / remounts from the same person collapse into ONE
          // live-visitor entry instead of showing as several "Active Now" cards.
          key: getVisitorId(),
        },
      },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        console.log('[Presence] Sync event received');
      })
      .subscribe(async (status, err) => {
        console.log('[Presence] Subscription status:', status);

        if (err) {
          console.error('[Presence] Subscription error:', err);
          return;
        }

        if (status === 'SUBSCRIBED') {
          console.log('[Presence] Connected — broadcasting initial presence');
          await trackPresence(channel);

          if (!refreshIntervalRef.current) {
            refreshIntervalRef.current = setInterval(() => {
              if (channelRef.current) {
                trackPresence(channelRef.current);
              }
            }, PRESENCE_REFRESH_INTERVAL);
          }
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error('[Presence] Channel error/timeout, scheduling reconnect...');
          destroyChannel();
          setTimeout(() => {
            if (!channelRef.current && !shouldExcludeFromAnalytics(pathnameRef.current)) {
              console.log('[Presence] Reconnecting after error...');
              initChannel();
            }
          }, 5000);
        }
      });

    channelRef.current = channel;
  }, [trackPresence, destroyChannel]);

  // 3. Main effect: create channel when ready AND on a non-excluded route
  useEffect(() => {
    if (isBotSession.current || !isReady) return;

    if (shouldExcludeFromAnalytics(pathnameRef.current)) {
      console.log('[Presence] Skipping channel creation on excluded route');
      return;
    }

    initChannel();

    return () => {
      destroyChannel();
    };
  }, [isReady, initChannel, destroyChannel]);

  // 4. Handle route changes: destroy on excluded, recreate on public
  useEffect(() => {
    if (!isReady || isBotSession.current) return;
    const isExcluded = shouldExcludeFromAnalytics(location.pathname);

    if (isExcluded) {
      if (channelRef.current) {
        console.log('[Presence] Navigated to excluded route, destroying channel');
        destroyChannel();
      }
      return;
    }

    if (!channelRef.current) {
      console.log('[Presence] Back on public route, recreating channel');
      initChannel();
    } else {
      console.log('[Presence] Page changed, updating presence');
      trackPresence(channelRef.current);
    }
  }, [location.pathname, isReady, trackPresence, initChannel, destroyChannel]);
}

// Helper to set visitor context from booking pages
export const setVisitorBookingContext = (bookingId: string, vbNumber: string, customerName?: string) => {
  sessionStorage.setItem(`booking_vb_${bookingId}`, vbNumber);
  if (customerName) {
    sessionStorage.setItem('visitor_customer_name', customerName);
  }
};

// Helper to set job context
export const setVisitorJobContext = (vbNumber: string) => {
  sessionStorage.setItem('current_job_vb', vbNumber);
};

// Helper to set VB context for resume booking page
export const setVisitorVBContext = (vbNumber: string) => {
  sessionStorage.setItem('pending_vb_lookup', vbNumber);
  console.log('[Presence] VB context set:', vbNumber);
};

// Helper to clear VB context
export const clearVisitorVBContext = () => {
  sessionStorage.removeItem('pending_vb_lookup');
};

export default useVisitorPresence;
